import { Camera, CheckCircle, DollarSign, FileText, Route, Truck } from "lucide-react";
import { getStatusButtonText, getStatusColor, getStatusDisplayText } from "../utilities/utilities";

// Course Card Component
export const CourseCard = ({ course, onShowDetails, onCMRUpload, onUpdateStatus }) => (
  <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md rounded-3xl border border-white/30 p-5 mb-4 shadow-xl hover:shadow-2xl transition-all duration-300 hover:border-white/40">
    {/* Header Section */}
    <div className="flex justify-between items-start mb-4">
      <div className="flex-1 min-w-0 pr-3">
        {/* Route */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="bg-blue-500/20 p-2 rounded-xl">
            <Route className="w-4 h-4 text-blue-400" />
          </div>
          <span className="text-white font-semibold text-base leading-tight truncate">
            {course.from}
          </span>
        </div>
        <div className="flex items-center space-x-2 mb-3 ml-10">
          <div className="h-6 w-0.5 bg-gradient-to-b from-blue-400 to-transparent -ml-8 -mt-2" />
          <span className="text-blue-300 font-semibold text-base">→ {course.to}</span>
        </div>
        
        {/* Client */}
        <p className="text-gray-200 text-sm font-medium mb-2 ml-2">{course.client}</p>
        
        {/* Vehicle */}
        {course.vehicleNumber && (
          <div className="flex items-center bg-gray-800/40 rounded-lg px-3 py-1.5 w-fit ml-2">
            <Truck className="w-3.5 h-3.5 text-gray-300 mr-2" />
            <span className="text-gray-300 text-xs font-medium">{course.vehicleNumber}</span>
          </div>
        )}
      </div>
      
      {/* Status & Payment */}
      <div className="flex flex-col items-end space-y-2.5 flex-shrink-0">
        <span className={`px-3 py-1.5 rounded-xl text-xs font-semibold border shadow-lg ${getStatusColor(course.status)}`}>
          {getStatusDisplayText(course.status)}
        </span>
        {course.paymentType === 'cash' && course.amount > 0 && (
          <div className="flex items-center bg-green-500/20 border border-green-500/30 rounded-xl px-3 py-1.5">
            <DollarSign className="w-3.5 h-3.5 text-green-400 mr-1" />
            <span className="text-green-400 text-xs font-bold">{course.amount.toLocaleString('ro-RO')} RON</span>
          </div>
        )}
      </div>
    </div>
    
    {/* CMR Status Badges */}
    <div className="mb-3 space-y-2">
      {course.cmrStartUploaded && (
        <div className="flex items-center bg-blue-500/20 border border-blue-500/30 rounded-xl px-3 py-2 w-fit">
          <CheckCircle className="w-4 h-4 text-blue-400 mr-2" />
          <span className="text-blue-400 text-xs font-semibold">CMR Pornire încărcat</span>
        </div>
      )}
      {course.cmrEndUploaded && (
        <div className="flex items-center bg-green-500/20 border border-green-500/30 rounded-xl px-3 py-2 w-fit">
          <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
          <span className="text-green-400 text-xs font-semibold">CMR Finalizare încărcat</span>
        </div>
      )}
    </div>
    
    {/* Action Buttons */}
    <div className="flex flex-col sm:flex-row gap-2.5">
      <button
        onClick={() => onShowDetails(course)}
        className="flex-1 bg-blue-500/25 hover:bg-blue-500/40 active:bg-blue-500/50 text-blue-200 border border-blue-400/40 rounded-2xl py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-blue-500/20"
      >
        <FileText className="w-4 h-4" />
        <span>Detalii</span>
      </button>
      
      {course.status !== 'livrat' && (
        <>
          {/* Show status change button ONLY for "in_asteptare" status */}
          {course.status === 'in_asteptare' && (
            <button
              onClick={() => onUpdateStatus(course.id, 'in_incarcare')}
              className="flex-1 bg-green-500/25 hover:bg-green-500/40 active:bg-green-500/50 text-green-200 border border-green-400/40 rounded-2xl py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-green-500/20"
            >
              <CheckCircle className="w-4 h-4" />
              <span>Marchează În Încărcare</span>
            </button>
          )}
          
          {/* Show CMR Start button ONLY if status is "in_incarcare" and CMR not uploaded */}
          {course.status === 'in_incarcare' && !course.cmrStartUploaded && (
            <button
              onClick={() => onCMRUpload(course.id, 'start')}
              className="flex-1 bg-orange-500/25 hover:bg-orange-500/40 active:bg-orange-500/50 text-orange-200 border border-orange-400/40 rounded-2xl py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-orange-500/20"
            >
              <Camera className="w-4 h-4" />
              <span>CMR Pornire</span>
            </button>
          )}
          
          {/* Show CMR End button ONLY if status is "pe_drum" and CMR not uploaded */}
          {course.status === 'pe_drum' && !course.cmrEndUploaded && (
            <button
              onClick={() => onCMRUpload(course.id, 'end')}
              className="flex-1 bg-purple-500/25 hover:bg-purple-500/40 active:bg-purple-500/50 text-purple-200 border border-purple-400/40 rounded-2xl py-3 px-4 text-sm font-semibold flex items-center justify-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-purple-500/20"
            >
              <Camera className="w-4 h-4" />
              <span>CMR Finalizare</span>
            </button>
          )}
        </>
      )}
    
    </div>
  </div>
);